import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CTA_GUARD_ACTION,
  CtaGuard,
  CtaGuardOutcome
} from '~/constants/common/ctaGuard';
import { mfes } from '~/constants/common/MfeModules';
import { useIsNative } from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import useMfeReady from '~/hooks/common/mfe/useMfeReady';
import {
  ACCOUNT_MODAL_MODULE,
  selectIsMfeLoaded,
  selectMfeLoadCount,
  useMfeLoadStore
} from '~/state/common/MfeLoadState';
import { logInfo } from '~/utilities/loggers';

// Stable selectors — defined at module level for referential stability across renders.
// Readiness comes from useMfeReady, which is true only after ACCOUNT_MODAL_READY has fired
// (not merely 'loaded') — the header MFE's signal that CoreAccountModal is safe to call.
const selectIsAccountModalLoaded = selectIsMfeLoaded(ACCOUNT_MODAL_MODULE);
const selectAccountModalLoadCount = selectMfeLoadCount(ACCOUNT_MODAL_MODULE);

export default function useAccountModalReady() {
  const isLoggedIn = useIsLoggedIn();
  const isNative = useIsNative();
  const isReady = useMfeReady(mfes.accountModal);
  const accountModalLoaded = useMfeLoadStore(selectIsAccountModalLoaded);
  const accountModalLoadCount = useMfeLoadStore(selectAccountModalLoadCount);
  const pendingRef = useRef<(() => void) | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Logged-in and native users never open the account modal, so they should
  // never wait for it. Only logged-out web users need deferred execution.
  const waitForAccountModal = !isLoggedIn && !isNative;

  useEffect(() => {
    if (isReady && pendingRef.current) {
      logInfo(CTA_GUARD_ACTION, {
        guard: CtaGuard.Readiness,
        outcome: CtaGuardOutcome.DeferredResolved
      });

      pendingRef.current();
      pendingRef.current = null;
      setIsPending(false);
    }
  }, [isReady]);

  const whenReady = useCallback(
    (action: () => void) => {
      if (!waitForAccountModal || isReady) {
        action();
      } else {
        datadogRum.addAction('vdp-purchase-cta-click-deferred', {
          accountModalLoaded,
          accountModalLoadCount
        });

        logInfo(CTA_GUARD_ACTION, {
          accountModalLoaded,
          accountModalLoadCount,
          guard: CtaGuard.Readiness,
          outcome: CtaGuardOutcome.Deferred
        });

        pendingRef.current = action;
        setIsPending(true);
      }
    },
    [accountModalLoadCount, accountModalLoaded, isReady, waitForAccountModal]
  );

  return { isPending: waitForAccountModal && isPending, isReady, whenReady };
}
